import { openAuthModal } from "@blocks/_shared/auth/use-auth-modal";
import Button from "@blocks/_shared/button";
import { useEnrollment } from "@blocks/_shared/hooks/use-enrollment";
import { EarnRulesGridSkeleton } from "@blocks/_shared/skeleton/patterns/earn-rules";
import apiFetch from "@wordpress/api-fetch";
import { createRoot } from "@wordpress/element";
import { __ } from "@wordpress/i18n";
/**
 * Tiers List Block - Frontend View
 *
 * Client-side rendering for all enrollment states.
 * Shows skeleton while loading, then renders appropriate state.
 */
import type React from "react";
import { useEffect, useState } from "react";
import { joinProgram } from "~/shared/queries";
import {
	type TierBadgeStyles,
	TiersListProvider,
	useTiersListContext,
} from "./context";
import "./style.scss";

// ============================================================================
// Types
// ============================================================================

interface Attributes {
	columns: number;
	cardLabelLevel: number;
	buttonTextJoin: string;
	buttonTextSignIn: string;
	badgeTextActive: string;
	badgeTextAchieved: string;
	showDescription: boolean;
	showPerks: boolean;
	imageHeight: number;
	imageFit: "contain" | "cover" | "fill";
	buttonTextColor?: string;
	buttonBackgroundColor?: string;
	badgeActiveBackgroundColor?: string;
	badgeActiveTextColor?: string;
	badgeAchievedBackgroundColor?: string;
	badgeAchievedTextColor?: string;
}

interface TierPerk {
	id: string;
	label: string;
}

interface Tier {
	id: string;
	name: string;
	position: number;
	media: {
		type: string;
		value: string;
	} | null;
	description?: string | null;
	perks?: TierPerk[];
}

// ============================================================================
// Main Component
// ============================================================================

const TiersListView = ({ attributes }: { attributes: Attributes }) => {
	const {
		status,
		tier: currentTier,
		isLoading: isEnrollmentLoading,
	} = useEnrollment();
	const [tiers, setTiers] = useState<Tier[]>([]);
	const [isLoadingTiers, setIsLoadingTiers] = useState(true);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		// Fetch tiers for all states except guest (guests get a sign-in CTA)
		if (status === "guest") {
			setIsLoadingTiers(false);
			return;
		}

		apiFetch<{ tiers: Tier[] }>({ path: "/leat/v1/tiers" })
			.then((response) => {
				const sortedTiers = response.tiers.sort(
					(a, b) => a.position - b.position,
				);
				setTiers(sortedTiers);
			})
			.catch((err) => {
				console.error("[Tiers List] API Error:", err);
				setError(err.message || "Failed to load tiers");
			})
			.finally(() => setIsLoadingTiers(false));
	}, [status]);

	if (isEnrollmentLoading || (status !== "guest" && isLoadingTiers)) {
		return <EarnRulesGridSkeleton columns={attributes.columns} />;
	}

	if (error) {
		return (
			<div className="leat-tiers-list__error">
				<p>{error}</p>
				<Button onClick={() => window.location.reload()} variant="contained">
					{__("Try Again", "leat-crm")}
				</Button>
			</div>
		);
	}

	switch (status) {
		case "guest":
			return <GuestState attributes={attributes} />;

		case "loggedInNotEnrolled":
			return <LoggedInNotEnrolledState attributes={attributes} tiers={tiers} />;

		case "enrolled":
			return (
				<EnrolledState
					attributes={attributes}
					tiers={tiers}
					currentTierId={currentTier?.id || null}
				/>
			);

		default:
			return null;
	}
};

// ============================================================================
// State Components
// ============================================================================

const buildActionButtonStyles = (
	attributes: Attributes,
): React.CSSProperties => {
	const styles: React.CSSProperties = {};
	if (attributes.buttonTextColor) styles.color = attributes.buttonTextColor;
	if (attributes.buttonBackgroundColor) {
		styles.backgroundColor = attributes.buttonBackgroundColor;
		styles.borderColor = "transparent";
	}
	return styles;
};

const buildBadgeStyles = (attributes: Attributes): TierBadgeStyles => {
	const active: React.CSSProperties = {};
	if (attributes.badgeActiveBackgroundColor)
		active.backgroundColor = attributes.badgeActiveBackgroundColor;
	if (attributes.badgeActiveTextColor)
		active.color = attributes.badgeActiveTextColor;

	const achieved: React.CSSProperties = {};
	if (attributes.badgeAchievedBackgroundColor)
		achieved.backgroundColor = attributes.badgeAchievedBackgroundColor;
	if (attributes.badgeAchievedTextColor)
		achieved.color = attributes.badgeAchievedTextColor;

	return { active, achieved };
};

const GuestState = ({ attributes }: { attributes: Attributes }) => {
	return (
		<div className="leat-tiers-list__empty-state">
			<p>
				{__(
					"Sign in to view our loyalty tiers and exclusive benefits.",
					"leat-crm",
				)}
			</p>
			<Button
				onClick={() => openAuthModal({ intent: "auto" })}
				variant="contained"
				style={buildActionButtonStyles(attributes)}
			>
				{attributes.buttonTextSignIn}
			</Button>
		</div>
	);
};

const LoggedInNotEnrolledState = ({
	attributes,
	tiers,
}: {
	attributes: Attributes;
	tiers: Tier[];
}) => {
	const [isJoining, setIsJoining] = useState(false);
	const [joinError, setJoinError] = useState<string | null>(null);

	const handleJoinProgram = async () => {
		setIsJoining(true);
		setJoinError(null);

		try {
			const userId = window.leat?.userId ?? null;

			if (!userId) {
				throw new Error("User ID not found");
			}

			await joinProgram(userId);
			window.location.reload();
		} catch (err) {
			const message =
				err instanceof Error ? err.message : "Failed to join program";
			setJoinError(message);
			setIsJoining(false);
		}
	};

	const buttonStyles = buildActionButtonStyles(attributes);

	return (
		<>
			<div className="leat-tiers-list__cta">
				<p>
					{__(
						"Join our loyalty program to unlock exclusive tier benefits.",
						"leat-crm",
					)}
				</p>
				<Button
					onClick={handleJoinProgram}
					disabled={isJoining}
					variant="contained"
					style={buttonStyles}
				>
					{isJoining ? __("Joining…", "leat-crm") : attributes.buttonTextJoin}
				</Button>
				{joinError && (
					<p className="leat-tiers-list__error-text">{joinError}</p>
				)}
			</div>

			{tiers.length > 0 && (
				<TierGrid
					attributes={attributes}
					tiers={tiers}
					currentTierPosition={null}
				/>
			)}
		</>
	);
};

const EnrolledState = ({
	attributes,
	tiers,
	currentTierId,
}: {
	attributes: Attributes;
	tiers: Tier[];
	currentTierId: string | null;
}) => {
	if (tiers.length === 0) {
		return (
			<div className="leat-tiers-list__empty-state">
				<p>{__("No tier levels available at this time.", "leat-crm")}</p>
			</div>
		);
	}

	const currentTier = tiers.find((t) => t.id === currentTierId);
	const currentTierPosition = currentTier ? currentTier.position : null;

	return (
		<TierGrid
			attributes={attributes}
			tiers={tiers}
			currentTierPosition={currentTierPosition}
		/>
	);
};

// ============================================================================
// Tier Grid
// ============================================================================

const TierGrid = ({
	attributes,
	tiers,
	currentTierPosition,
}: {
	attributes: Attributes;
	tiers: Tier[];
	currentTierPosition: number | null;
}) => {
	const contextValue = {
		currentTierPosition,
		labelLevel: attributes.cardLabelLevel,
		showDescription: attributes.showDescription,
		showPerks: attributes.showPerks,
		imageHeight: attributes.imageHeight,
		imageFit: attributes.imageFit,
		badgeTexts: {
			active: attributes.badgeTextActive,
			achieved: attributes.badgeTextAchieved,
		},
		badgeStyles: buildBadgeStyles(attributes),
	};

	const gridStyle: React.CSSProperties = {
		gridTemplateColumns: `repeat(${attributes.columns}, 1fr)`,
	};

	return (
		<TiersListProvider value={contextValue}>
			<div className="leat-tiers-list__grid" style={gridStyle}>
				{tiers.map((tier) => (
					<TierCard key={tier.id} tier={tier} />
				))}
			</div>
		</TiersListProvider>
	);
};

// ============================================================================
// Tier Card
// ============================================================================

type BadgeKind = "active" | "achieved" | null;

const determineBadge = (
	tierPosition: number,
	currentTierPosition: number | null,
): BadgeKind => {
	if (currentTierPosition === null) return null;
	if (tierPosition === currentTierPosition) return "active";
	if (tierPosition < currentTierPosition) return "achieved";
	return null;
};

const TierCard = ({ tier }: { tier: Tier }) => {
	const {
		currentTierPosition,
		labelLevel,
		showDescription,
		showPerks,
		imageHeight,
		imageFit,
		badgeTexts,
		badgeStyles,
	} = useTiersListContext();

	const badge = determineBadge(tier.position, currentTierPosition);
	const LabelTag = `h${labelLevel}` as keyof JSX.IntrinsicElements;

	const cardStyle = {
		"--leat-tier-image-height": `${imageHeight}px`,
		"--leat-tier-image-fit": imageFit,
	} as React.CSSProperties;

	return (
		<article
			className="leat-tier-card"
			data-state={badge ?? "upcoming"}
			style={cardStyle}
		>
			<div className="leat-tier-card__media">{renderTierMedia(tier)}</div>

			<header className="leat-tier-card__header">
				<div className="leat-tier-card__heading">
					<LabelTag className="leat-tier-card__name">{tier.name}</LabelTag>
					{showDescription && tier.description && (
						<p className="leat-tier-card__subtitle">{tier.description}</p>
					)}
				</div>

				{badge && (
					<TierBadge
						kind={badge}
						label={badge === "active" ? badgeTexts.active : badgeTexts.achieved}
						style={
							badge === "active" ? badgeStyles.active : badgeStyles.achieved
						}
					/>
				)}
			</header>

			{showPerks && tier.perks && tier.perks.length > 0 && (
				<>
					<div className="leat-tier-card__separator" aria-hidden="true" />
					<ul
						className="leat-tier-card__perks"
						aria-label={__("Perks included with this tier", "leat-crm")}
					>
						{tier.perks.map((perk) => (
							<li key={perk.id} className="leat-tier-card__perk">
								<CheckIcon />
								<span>{perk.label}</span>
							</li>
						))}
					</ul>
				</>
			)}
		</article>
	);
};

const renderTierMedia = (tier: Tier) => {
	if (!tier.media || !tier.media.value) {
		return (
			<div className="leat-tier-card__media-icon" aria-hidden="true">
				<span className="leat-tier-card__media-fallback">🏆</span>
			</div>
		);
	}

	if (tier.media.type === "svg") {
		return (
			<div
				className="leat-tier-card__media-icon"
				// biome-ignore lint/security/noDangerouslySetInnerHtml: controlled SVG content from backend
				dangerouslySetInnerHTML={{ __html: tier.media.value }}
			/>
		);
	}

	if (tier.media.type === "emoji") {
		return (
			<div className="leat-tier-card__media-icon" aria-hidden="true">
				<span className="leat-tier-card__media-emoji">{tier.media.value}</span>
			</div>
		);
	}

	return (
		<img
			className="leat-tier-card__media-image"
			src={tier.media.value}
			alt=""
		/>
	);
};

const TierBadge = ({
	kind,
	label,
	style,
}: {
	kind: "active" | "achieved";
	label: string;
	style?: React.CSSProperties;
}) => {
	return (
		<span
			className={`leat-tier-card__badge leat-tier-card__badge--${kind}`}
			style={style}
		>
			<CheckIcon />
			<span>{label}</span>
		</span>
	);
};

const CheckIcon = () => (
	<svg
		width="14"
		height="14"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2.5"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<polyline points="20 6 9 17 4 12" />
	</svg>
);

// ============================================================================
// Initialization
// ============================================================================

export function initTiersListBlocks() {
	const blocks = document.querySelectorAll<HTMLElement>(".leat-tiers-list");

	for (const block of blocks) {
		const attributes: Attributes = {
			columns: Number(block.dataset.columns) || 3,
			cardLabelLevel: Number(block.dataset.cardLabelLevel) || 4,
			buttonTextJoin: block.dataset.buttonTextJoin || "Join Program",
			buttonTextSignIn: block.dataset.buttonTextSignIn || "Sign In to Join",
			badgeTextActive: block.dataset.badgeTextActive || "Active tier",
			badgeTextAchieved: block.dataset.badgeTextAchieved || "Achieved",
			showDescription: block.dataset.showDescription !== "false",
			showPerks: block.dataset.showPerks !== "false",
			imageHeight: Number(block.dataset.imageHeight) || 200,
			imageFit:
				(block.dataset.imageFit as "contain" | "cover" | "fill") || "cover",
			buttonTextColor: block.dataset.buttonTextColor || undefined,
			buttonBackgroundColor: block.dataset.buttonBackgroundColor || undefined,
			badgeActiveBackgroundColor:
				block.dataset.badgeActiveBackgroundColor || undefined,
			badgeActiveTextColor: block.dataset.badgeActiveTextColor || undefined,
			badgeAchievedBackgroundColor:
				block.dataset.badgeAchievedBackgroundColor || undefined,
			badgeAchievedTextColor: block.dataset.badgeAchievedTextColor || undefined,
		};

		createRoot(block).render(<TiersListView attributes={attributes} />);
	}
}

if (document.readyState === "loading") {
	document.addEventListener("DOMContentLoaded", initTiersListBlocks);
} else {
	initTiersListBlocks();
}
